import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SelectInput, useEntityList } from "@pericles/framework-frontend";

import {
  exportRoadmapProjectBriefPdf,
  exportRoadmapUseCaseToJarvis,
  exportRoadmapProcessPdf,
  fetchRoadmapReviewViewConfig,
  fetchRoadmapAnalysisFields,
  fetchRoadmapData,
  fetchRoadmapProcess,
  fetchRoadmapProcesses,
  fetchRoadmapProcessUseCases,
  fetchRoadmapProjects,
  fetchRoadmapUseCase,
  fetchRoadmapUseCaseParentProcess,
  fetchRoadmapUseCaseDigests,
  generateRoadmapUseCaseDigests,
  generateRoadmapUseCaseEnrichments,
  fetchRoadmapUseCaseEnrichments,
  updateRoadmapUseCaseEnrichment,
  isMigrationsRequiredError,
  saveRoadmapReviewViewConfig,
  updateRoadmapProcess,
  updateRoadmapUseCase,
  validateRoadmapFirstReview,
  type RoadmapBriefAnalysisFields,
  type RoadmapAppRecord,
  type RoadmapIntegrationRecord,
  type RoadmapProcessDetail,
  type RoadmapProcessRecord,
  type RoadmapProcessSummary,
  type RoadmapMigrationsRequiredDetails,
  type RoadmapReviewViewConfig,
  type RoadmapReviewViewFieldConfig,
  type RoadmapFirstReviewGate,
  type RoadmapFirstReviewValidationDetails,
  type RoadmapUseCaseDetail,
  type RoadmapUseCaseRecord,
  type RoadmapUseCaseSummary,
  type RoadmapUseCaseJarvisExport,
  type RoadmapProject,
  type RoadmapRole,
  type RoadmapUserRecord,
  type RoadmapUseCaseDigestRecord,
  type RoadmapUseCaseDigestSummary,
  type RoadmapUseCaseEnrichmentRecord,
} from "../api/roadmapClient";

import {
  Badge,
  Divider,
  EmptyState,
  IconButton,
  Pane,
  PaneHeader,
  PageHeader,
  Section,
  Skeleton,
  Tag,
} from "./roadmap/DesignSystem";
import { AtlasMigrationsRequiredCallout } from "./roadmap/AtlasMigrationsRequiredCallout";
import { ProcessDetailPanel, type ProcessDetailPanelData } from "./roadmap/ProcessDetailPanel";
import { UseCaseConversationPanel } from "./roadmap/UseCaseConversationPanel";
import { UseCaseDetailPanel, type UseCaseDetailPanelData } from "./roadmap/UseCaseDetailPanel";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "./ui";

const PROCESS_LIST_ROW_HEIGHT = 64;
const SEARCH_DEBOUNCE_MS = 300;

const SORT_OPTIONS = [
  { value: "updated", label: "Updated" },
  { value: "name", label: "Name" },
  { value: "useCaseCount", label: "Use case count" },
  { value: "score", label: "Score" },
  { value: "status", label: "Status" },
  { value: "owner", label: "Owner" },
  { value: "department", label: "Department" },
  { value: "entity", label: "Entity" },
];

const DEFAULT_PROCESS_REVIEW_FIELDS: Array<{ key: string; label: string }> = [
  { key: "name", label: "Name" },
  { key: "purpose", label: "Purpose" },
  { key: "reviewOwner", label: "Owner" },
  { key: "reviewStatus", label: "Review status" },
  { key: "reviewNotes", label: "Review notes" },
  { key: "entity", label: "Entity" },
  { key: "department", label: "Department" },
  { key: "status", label: "Status" },
  { key: "applicability", label: "Applicability" },
  { key: "steps", label: "Steps" },
  { key: "systems", label: "Systems involved" },
  { key: "risks", label: "Risks & exceptions" },
];

const DEFAULT_USE_CASE_REVIEW_FIELDS: Array<{ key: string; label: string }> = [
  { key: "name", label: "Title" },
  { key: "problem", label: "Problem" },
  { key: "outcome", label: "Outcome" },
  { key: "reviewOwner", label: "Owner" },
  { key: "reviewStatus", label: "Review status" },
  { key: "reviewNotes", label: "Review notes" },
  { key: "status", label: "Status" },
  { key: "aiOutputs", label: "AI outputs" },
  { key: "humanValidation", label: "Human validation" },
  { key: "acceptanceCriteria", label: "Acceptance criteria" },
  { key: "notes", label: "Notes" },
  { key: "aiPriorityScore", label: "AI priority score" },
  { key: "opportunityScore", label: "Opportunity score" },
  { key: "impact", label: "Impact" },
  { key: "feasibility", label: "Feasibility" },
  { key: "risk", label: "Risk" },
  { key: "estimatedDuration", label: "Estimated duration" },
  { key: "estimatedCost", label: "Estimated cost" },
  { key: "plannedStart", label: "Planned start" },
  { key: "plannedEnd", label: "Planned end" },
  { key: "targetArchitecture", label: "Target architecture" },
  { key: "reviewStage", label: "Review stage" },
];

const REVIEW_STATUS_OPTIONS = [
  { value: "to_be_reviewed_by_po", label: "To be reviewed by PO" },
  { value: "in_review", label: "In review" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "validated", label: "Validated" },
  { value: "rejected", label: "Rejected" },
];

function buildReviewViewDefaults(
  fields: Array<{ key: string; label: string }>,
): RoadmapReviewViewFieldConfig[] {
  return fields.map((field, index) => ({ ...field, enabled: true, order: index + 1 }));
}

const DEFAULT_REVIEW_VIEW_CONFIG: RoadmapReviewViewConfig = {
  processFields: buildReviewViewDefaults(DEFAULT_PROCESS_REVIEW_FIELDS),
  useCaseFields: buildReviewViewDefaults(DEFAULT_USE_CASE_REVIEW_FIELDS),
};

const ROADMAP_ROLE_PERMISSIONS: Record<RoadmapRole, { edit: boolean; review: boolean }> = {
  VIEWER: { edit: false, review: false },
  EDITOR: { edit: true, review: false },
  REVIEWER: { edit: false, review: true },
  ADMIN: { edit: true, review: true },
};

type CustomerListItem = { id: string | number; name?: string };

type UrlSelection = { processId: string; useCaseId: string };

const REVIEW_VALID_STATUSES = new Set(["validated"]);

function isReviewStatusValidated(value: string | null | undefined): boolean {
  if (!value) return false;
  return REVIEW_VALID_STATUSES.has(value.trim().toLowerCase());
}

function useViewportHeight(ref: React.RefObject<HTMLDivElement>): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () => setHeight(element.clientHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return height;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);

  return debounced;
}

function selectionFromUrl(): UrlSelection {
  const params = new URLSearchParams(window.location.search);
  return {
    processId: params.get("processId") ?? "",
    useCaseId: params.get("useCaseId") ?? "",
  };
}

function updateUrlSelection(processId: string, useCaseId: string): void {
  const params = new URLSearchParams(window.location.search);
  if (processId) {
    params.set("processId", processId);
  } else {
    params.delete("processId");
  }
  if (useCaseId) {
    params.set("useCaseId", useCaseId);
  } else {
    params.delete("useCaseId");
  }
  const query = params.toString();
  const nextUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
  window.history.replaceState({}, "", nextUrl);
}

export function AiRoadmapReviewScreen(): JSX.Element {
  const [projects, setProjects] = useState<RoadmapProject[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeCustomer") ?? "";
  });
  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeProject") ?? "";
  });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("updated");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [processes, setProcesses] = useState<RoadmapProcessSummary[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);
  const [processesError, setProcessesError] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<"review" | "fields">("review");
  const [reviewViewConfig, setReviewViewConfig] = useState<RoadmapReviewViewConfig | null>(null);
  const [reviewViewSaving, setReviewViewSaving] = useState(false);
  const [reviewViewNotice, setReviewViewNotice] = useState<string | null>(null);
  const [reviewViewError, setReviewViewError] = useState<string | null>(null);
  const [processFieldSearch, setProcessFieldSearch] = useState("");
  const [useCaseFieldSearch, setUseCaseFieldSearch] = useState("");
  const [draggedField, setDraggedField] = useState<{ scope: "process" | "useCase"; key: string } | null>(
    null,
  );
  const [processDetail, setProcessDetail] = useState<RoadmapProcessDetail | null>(null);
  const [processDetailLoading, setProcessDetailLoading] = useState(false);
  const [processDetailError, setProcessDetailError] = useState<string | null>(null);
  const [useCases, setUseCases] = useState<RoadmapUseCaseSummary[]>([]);
  const [useCasesLoading, setUseCasesLoading] = useState(false);
  const [useCasesError, setUseCasesError] = useState<string | null>(null);
  const [useCaseDetail, setUseCaseDetail] = useState<RoadmapUseCaseDetail | null>(null);
  const [useCaseDetailLoading, setUseCaseDetailLoading] = useState(false);
  const [useCaseDetailError, setUseCaseDetailError] = useState<string | null>(null);
  const [roadmapUsers, setRoadmapUsers] = useState<RoadmapUserRecord[]>([]);
  const [roadmapApps, setRoadmapApps] = useState<RoadmapAppRecord[]>([]);
  const [roadmapIntegrations, setRoadmapIntegrations] = useState<RoadmapIntegrationRecord[]>([]);
  const [roadmapProcessRecords, setRoadmapProcessRecords] = useState<RoadmapProcessRecord[]>([]);
  const [roadmapUseCaseRecords, setRoadmapUseCaseRecords] = useState<RoadmapUseCaseRecord[]>([]);
  const [selectedProcessId, setSelectedProcessId] = useState(() => selectionFromUrl().processId);
  const [selectedUseCaseId, setSelectedUseCaseId] = useState(() => selectionFromUrl().useCaseId);
  const [pendingUrlSelection, setPendingUrlSelection] = useState<UrlSelection | null>(() => selectionFromUrl());
  const [needsUseCaseValidation, setNeedsUseCaseValidation] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [useCaseDialogOpen, setUseCaseDialogOpen] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);
  const [analysisFieldsMigration, setAnalysisFieldsMigration] =
    useState<RoadmapMigrationsRequiredDetails | null>(null);
  const [jarvisExporting, setJarvisExporting] = useState(false);
  const [jarvisExportNotice, setJarvisExportNotice] = useState<string | null>(null);
  const [jarvisExportError, setJarvisExportError] = useState<string | null>(null);
  const [jarvisExportPayload, setJarvisExportPayload] = useState<RoadmapUseCaseJarvisExport | null>(null);
  const [reviewMetrics, setReviewMetrics] = useState({ total: 0, validated: 0, pending: 0 });
  const [firstReviewGate, setFirstReviewGate] = useState<RoadmapFirstReviewGate | null>(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationDetails, setValidationDetails] = useState<RoadmapFirstReviewValidationDetails | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [digestSummary, setDigestSummary] = useState<RoadmapUseCaseDigestSummary | null>(null);
  const [digestRecords, setDigestRecords] = useState<RoadmapUseCaseDigestRecord[]>([]);
  const [digestStatusCounts, setDigestStatusCounts] = useState<Record<string, number> | null>(null);
  const [digestRunning, setDigestRunning] = useState(false);
  const [digestError, setDigestError] = useState<string | null>(null);
  const [enrichments, setEnrichments] = useState<RoadmapUseCaseEnrichmentRecord[]>([]);
  const [enrichmentRunning, setEnrichmentRunning] = useState(false);
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const previousProjectId = useRef<string | null>(null);
  const viewportHeight = useViewportHeight(listRef);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const processCache = useRef(new Map<string, RoadmapProcessDetail>());
  const useCaseCache = useRef(new Map<string, RoadmapUseCaseDetail>());
  const getDeterministicProcessId = useCallback(() => processes[0]?.id ?? "", [processes]);

  const roadmapUserLookup = useMemo(() => {
    const byId = new Map<string, RoadmapUserRecord>();
    const byAirtableId = new Map<string, RoadmapUserRecord>();
    roadmapUsers.forEach((user) => {
      byId.set(user.id, user);
      if (user.airtableRecordId) {
        byAirtableId.set(user.airtableRecordId, user);
      }
    });
    return { byId, byAirtableId };
  }, [roadmapUsers]);

  const resolveOwnerName = useCallback(
    (ownerId: string | null | undefined): string | null => {
      if (!ownerId) return null;
      const user = roadmapUserLookup.byId.get(ownerId) ?? roadmapUserLookup.byAirtableId.get(ownerId);
      const rawRecord = user?.raw ?? {};
      const email =
        (typeof rawRecord.Email === "string" && rawRecord.Email.trim()) ||
        (typeof rawRecord.email === "string" && rawRecord.email.trim()) ||
        (typeof rawRecord["Email address"] === "string" && rawRecord["Email address"].trim()) ||
        null;
      if (user?.displayName && email) return `${user.displayName} (${email})`;
      if (user?.displayName) return user.displayName;
      if (typeof rawRecord.Name === "string" && rawRecord.Name.trim()) return rawRecord.Name;
      if (email) return email;
      return `Unknown user (id: ${ownerId})`;
    },
    [roadmapUserLookup],
  );

  const loadUseCaseDigests = useCallback(async () => {
    if (!selectedProjectId) {
      setDigestRecords([]);
      setDigestStatusCounts(null);
      setDigestSummary(null);
      return;
    }
    setDigestError(null);
    try {
      const result = await fetchRoadmapUseCaseDigests(selectedProjectId);
      setDigestRecords(result.digests);
      const counts = result.digests.reduce<Record<string, number>>((acc, digest) => {
        acc[digest.status] = (acc[digest.status] ?? 0) + 1;
        return acc;
      }, {});
      setDigestStatusCounts(counts);
    } catch (error) {
      console.error(error);
      setDigestStatusCounts(null);
      setDigestRecords([]);
      setDigestError("Impossible de charger les digests.");
    }
  }, [selectedProjectId]);

  const handleGenerateUseCaseDigests = useCallback(async () => {
    if (!selectedProjectId) return;
    setDigestRunning(true);
    setDigestError(null);
    try {
      const result = await generateRoadmapUseCaseDigests(selectedProjectId);
      setDigestSummary(result.summary);
      await loadUseCaseDigests();
    } catch (error) {
      console.error(error);
      setDigestError("Impossible de générer les digests.");
    } finally {
      setDigestRunning(false);
    }
  }, [loadUseCaseDigests, selectedProjectId]);

  const handleSaveEstimates = useCallback(
    async (payload: {
      estimatedDuration: number | null;
      estimatedCost: number | null;
      plannedStart: string | null;
      plannedEnd: string | null;
    }) => {
      if (!useCaseDetail) return;
      const updated = await updateRoadmapUseCase(useCaseDetail.id, payload);
      setUseCaseDetail(updated);
      useCaseCache.current.set(updated.id, updated);
    },
    [useCaseDetail],
  );

  const handleUpdateProcessReview = useCallback(
    async (payload: { reviewStatus?: string | null; reviewNotes?: string | null; reviewOwner?: string | null }) => {
      if (!processDetail) return;
      const updated = await updateRoadmapProcess(processDetail.id, payload);
      setProcessDetail(updated);
      processCache.current.set(updated.id, updated);
    },
    [processDetail],
  );

  const handleUpdateProcessDetails = useCallback(
    async (payload: {
      name?: string | null;
      entity?: string | null;
      department?: string | null;
      status?: string | null;
      purpose?: string | null;
      applicability?: string | null;
      steps?: string[] | null;
      risks?: string[] | null;
      systems?: string[] | null;
    }) => {
      if (!processDetail) return;
      const updated = await updateRoadmapProcess(processDetail.id, payload);
      setProcessDetail(updated);
      processCache.current.set(updated.id, updated);
    },
    [processDetail],
  );

  const handleUpdateUseCaseReview = useCallback(
    async (payload: { reviewStatus?: string | null; reviewNotes?: string | null; reviewOwner?: string | null }) => {
      if (!useCaseDetail) return;
      const updated = await updateRoadmapUseCase(useCaseDetail.id, payload);
      setUseCaseDetail(updated);
      useCaseCache.current.set(updated.id, updated);
    },
    [useCaseDetail],
  );

  const handleUpdateUseCaseDetails = useCallback(
    async (payload: {
      name?: string | null;
      status?: string | null;
      problem?: string | null;
      outcome?: string | null;
      aiOutputs?: string | null;
      humanValidation?: string | null;
      acceptanceCriteria?: string[] | null;
      notes?: string | null;
      opportunityScore?: number | null;
      impact?: number | null;
      feasibility?: number | null;
      risk?: number | null;
    }) => {
      if (!useCaseDetail) return;
      const updated = await updateRoadmapUseCase(useCaseDetail.id, payload);
      setUseCaseDetail(updated);
      useCaseCache.current.set(updated.id, updated);
    },
    [useCaseDetail],
  );

  const handleJarvisExport = useCallback(async () => {
    if (!useCaseDetail) return;
    setJarvisExporting(true);
    setJarvisExportNotice(null);
    setJarvisExportError(null);
    try {
      const response = await exportRoadmapUseCaseToJarvis(useCaseDetail.id);
      setJarvisExportPayload(response.export);
      setJarvisExportNotice(`Export Jarvis prêt pour ${response.export.title}.`);
      setUseCaseDetail((current) => (current ? { ...current, ...response.useCase } : current));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible d'exporter vers Jarvis.";
      setJarvisExportError(message);
    } finally {
      setJarvisExporting(false);
    }
  }, [useCaseDetail]);

  const handleValidateFirstReview = useCallback(async () => {
    if (!selectedProjectId) return;
    setValidationError(null);
    const result = await validateRoadmapFirstReview(selectedProjectId);
    if (result.ok && result.gate) {
      setFirstReviewGate(result.gate);
      setValidationDetails(null);
      setShowValidationDialog(false);
      window.dispatchEvent(new Event("roadmap:first-review-validated"));
      return;
    }
    if (result.error === "NOT_ALL_VALIDATED") {
      setValidationDetails(result.details ?? null);
      setShowValidationDialog(true);
      return;
    }
    setValidationError(result.error ?? "Impossible de valider la 1st Review.");
  }, [selectedProjectId]);

  const handleForceValidateFirstReview = useCallback(async () => {
    if (!selectedProjectId) return;
    setValidationError(null);
    const result = await validateRoadmapFirstReview(selectedProjectId, { force: true });
    if (result.ok && result.gate) {
      setFirstReviewGate(result.gate);
      setValidationDetails(null);
      setShowValidationDialog(false);
      window.dispatchEvent(new Event("roadmap:first-review-validated"));
      return;
    }
    setValidationError(result.error ?? "Impossible de valider la 1st Review.");
  }, [selectedProjectId]);

  const { data: customerOptionsData } = useEntityList<CustomerListItem>({
    entity: "Customer",
    page: 0,
    pageSize: 1000,
  });

  const customerOptions = useMemo(() => {
    const options = (customerOptionsData ?? []).map((customer) => ({
      value: String(customer.id),
      label: customer.name ?? String(customer.id),
    }));
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [customerOptionsData]);

  const projectOptions = useMemo(() => {
    return projects.map((project) => ({ value: project.id, label: project.name }));
  }, [projects]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) ?? null,
    [projects, selectedProjectId],
  );
  const selectedRole = (selectedProject?.role ?? "VIEWER") as RoadmapRole;
  const canReview = ROADMAP_ROLE_PERMISSIONS[selectedRole]?.review ?? false;
  const canEdit = ROADMAP_ROLE_PERMISSIONS[selectedRole]?.edit ?? false;
  const canEditReviewView = canReview || canEdit;
  const isAdmin = selectedRole === "ADMIN";

  useEffect(() => {
    if (!selectedProject) {
      setFirstReviewGate(null);
      return;
    }
    setFirstReviewGate({
      validatedAt: selectedProject.firstReviewValidatedAt ?? null,
      validatedByUserId: selectedProject.firstReviewValidatedByUserId ?? null,
      forceValidated: Boolean(selectedProject.firstReviewForceValidated),
      notes: selectedProject.firstReviewValidationNotes ?? null,
    });
  }, [selectedProject]);

  useEffect(() => {
    void loadUseCaseDigests();
  }, [loadUseCaseDigests]);

  useEffect(() => {
    const handlePop = () => {
      const selection = selectionFromUrl();
      setPendingUrlSelection(selection);
      setNeedsUseCaseValidation(false);
      setSelectedProcessId(selection.processId);
      setSelectedUseCaseId(selection.useCaseId);
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  useEffect(() => {
    setJarvisExportNotice(null);
    setJarvisExportError(null);
    setJarvisExportPayload(null);
    setJarvisExporting(false);
  }, [useCaseDetail?.id]);

  useEffect(() => {
    if (!selectedCustomerId) {
      setProjects([]);
      setSelectedProjectId("");
      return;
    }
    localStorage.setItem("studio.aiRoadmap.activeCustomer", selectedCustomerId);
    let isActive = true;
    fetchRoadmapProjects(selectedCustomerId)
      .then((loaded) => {
        if (!isActive) return;
        setProjects(loaded);
        if (!loaded.length) {
          setSelectedProjectId("");
          return;
        }
        if (!loaded.find((project) => project.id === selectedProjectId)) {
          setSelectedProjectId(loaded[0].id);
        }
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        setProjects([]);
      });
    return () => {
      isActive = false;
    };
  }, [selectedCustomerId, selectedProjectId]);

  useEffect(() => {
    if (selectedProjectId) {
      localStorage.setItem("studio.aiRoadmap.activeProject", selectedProjectId);
      window.dispatchEvent(new Event("roadmap:project-changed"));
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (previousProjectId.current === null) {
      previousProjectId.current = selectedProjectId;
      return;
    }
    if (previousProjectId.current !== selectedProjectId) {
      setSelectedProcessId("");
      setSelectedUseCaseId("");
      setProcessDetail(null);
      setUseCaseDetail(null);
      setUseCases([]);
      setRoadmapUsers([]);
      setRoadmapApps([]);
      setRoadmapIntegrations([]);
      setRoadmapProcessRecords([]);
      setRoadmapUseCaseRecords([]);
      processCache.current.clear();
      useCaseCache.current.clear();
      updateUrlSelection("", "");
      setPendingUrlSelection(null);
      setNeedsUseCaseValidation(false);
      setProcessDialogOpen(false);
      setUseCaseDialogOpen(false);
    }
    previousProjectId.current = selectedProjectId;
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return;
    let isActive = true;
    fetchRoadmapData(selectedProjectId)
      .then((data) => {
        if (!isActive) return;
        setRoadmapUsers(data.users ?? []);
        setRoadmapApps(data.apps ?? []);
        setRoadmapIntegrations(data.integrations ?? []);
        setRoadmapProcessRecords(data.processes ?? []);
        setRoadmapUseCaseRecords(data.useCases ?? []);
        setEnrichments(data.enrichments ?? []);
        const processes = data.processes ?? [];
        const useCasesData = data.useCases ?? [];
        const validatedCount =
          processes.filter((process) => isReviewStatusValidated(process.reviewStatus)).length +
          useCasesData.filter((useCase) => isReviewStatusValidated(useCase.reviewStatus)).length;
        const totalCount = processes.length + useCasesData.length;
        setReviewMetrics({
          total: totalCount,
          validated: validatedCount,
          pending: Math.max(totalCount - validatedCount, 0),
        });
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        setRoadmapUsers([]);
        setRoadmapApps([]);
        setRoadmapIntegrations([]);
        setRoadmapProcessRecords([]);
        setRoadmapUseCaseRecords([]);
        setReviewMetrics({ total: 0, validated: 0, pending: 0 });
      });
    return () => {
      isActive = false;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setReviewViewConfig(null);
      return;
    }
    let isActive = true;
    setReviewViewError(null);
    setReviewViewNotice(null);
    fetchRoadmapReviewViewConfig(selectedProjectId)
      .then((config) => {
        if (!isActive) return;
        setReviewViewConfig(config);
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        setReviewViewError("Impossible de charger la configuration des champs.");
      });
    return () => {
      isActive = false;
    };
  }, [selectedProjectId]);

  const processFieldConfig = useMemo(
    () => reviewViewConfig?.processFields ?? DEFAULT_REVIEW_VIEW_CONFIG.processFields,
    [reviewViewConfig],
  );
  const useCaseFieldConfig = useMemo(
    () => reviewViewConfig?.useCaseFields ?? DEFAULT_REVIEW_VIEW_CONFIG.useCaseFields,
    [reviewViewConfig],
  );

  const updateReviewViewConfig = useCallback(
    (scope: "process" | "useCase", updater: (current: RoadmapReviewViewConfig) => RoadmapReviewViewConfig) => {
      setReviewViewConfig((current) => {
        const base = current ?? DEFAULT_REVIEW_VIEW_CONFIG;
        return updater(base);
      });
    },
    [],
  );

  const handleToggleField = useCallback(
    (scope: "process" | "useCase", fieldKey: string) => {
      updateReviewViewConfig(scope, (current) => {
        const fields = scope === "process" ? current.processFields : current.useCaseFields;
        const updated = fields.map((field) =>
          field.key === fieldKey ? { ...field, enabled: !field.enabled } : field,
        );
        return scope === "process"
          ? { ...current, processFields: updated }
          : { ...current, useCaseFields: updated };
      });
    },
    [updateReviewViewConfig],
  );

  const handleMoveField = useCallback(
    (scope: "process" | "useCase", fieldKey: string, direction: -1 | 1) => {
      updateReviewViewConfig(scope, (current) => {
        const fields = scope === "process" ? current.processFields : current.useCaseFields;
        const ordered = [...fields].sort((a, b) => a.order - b.order);
        const index = ordered.findIndex((field) => field.key === fieldKey);
        const nextIndex = index + direction;
        if (index === -1 || nextIndex < 0 || nextIndex >= ordered.length) return current;
        const nextOrder = [...ordered];
        const [moved] = nextOrder.splice(index, 1);
        nextOrder.splice(nextIndex, 0, moved);
        const reordered = nextOrder.map((field, orderIndex) => ({ ...field, order: orderIndex + 1 }));
        return scope === "process"
          ? { ...current, processFields: reordered }
          : { ...current, useCaseFields: reordered };
      });
    },
    [updateReviewViewConfig],
  );

  const handleSaveReviewViewConfig = useCallback(async () => {
    if (!selectedProjectId || !reviewViewConfig) return;
    setReviewViewSaving(true);
    setReviewViewError(null);
    try {
      const saved = await saveRoadmapReviewViewConfig(selectedProjectId, reviewViewConfig);
      setReviewViewConfig(saved);
      setReviewViewNotice("Champs enregistrés.");
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : "Impossible d'enregistrer les champs.";
      setReviewViewError(message);
    } finally {
      setReviewViewSaving(false);
      window.setTimeout(() => setReviewViewNotice(null), 2000);
    }
  }, [reviewViewConfig, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setProcesses([]);
      return;
    }
    let isActive = true;
    setProcessesLoading(true);
    setProcessesError(null);
    fetchRoadmapProcesses({
      projectId: selectedProjectId,
      search: debouncedSearch,
      sort,
      limit: 500,
      offset: 0,
    })
      .then((data) => {
        if (!isActive) return;
        setProcesses(data);
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        setProcessesError("Impossible de charger les processus.");
      })
      .finally(() => {
        if (!isActive) return;
        setProcessesLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [selectedProjectId, debouncedSearch, sort]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
    setScrollTop(0);
  }, [selectedProjectId, debouncedSearch, sort]);

  useEffect(() => {
    if (!selectedProjectId || processesLoading || !pendingUrlSelection || !processes.length) return;
    let isActive = true;
    const { processId, useCaseId } = pendingUrlSelection;
    const processExists = processId && processes.some((process) => process.id === processId);

    const applySelection = (nextProcessId: string, nextUseCaseId: string) => {
      if (!isActive) return;
      setSelectedProcessId(nextProcessId);
      setSelectedUseCaseId(nextUseCaseId);
      setProcessDialogOpen(Boolean(nextProcessId));
      setUseCaseDialogOpen(Boolean(nextUseCaseId));
      updateUrlSelection(nextProcessId, nextUseCaseId);
    };

    if (processExists) {
      applySelection(processId, useCaseId);
      setNeedsUseCaseValidation(Boolean(useCaseId));
      setPendingUrlSelection(null);
      return () => {
        isActive = false;
      };
    }

    if (useCaseId) {
      void fetchRoadmapUseCaseParentProcess(useCaseId)
        .then((parentProcessId) => {
          if (!isActive) return;
          if (parentProcessId && processes.some((process) => process.id === parentProcessId)) {
            applySelection(parentProcessId, useCaseId);
          } else {
            const fallbackProcessId = getDeterministicProcessId();
            applySelection(fallbackProcessId, "");
          }
          setPendingUrlSelection(null);
        })
        .catch((error) => {
          console.error(error);
          if (!isActive) return;
          const fallbackProcessId = getDeterministicProcessId();
          applySelection(fallbackProcessId, "");
          setPendingUrlSelection(null);
        });
      return () => {
        isActive = false;
      };
    }

    const fallbackProcessId = getDeterministicProcessId();
    applySelection(fallbackProcessId, "");
    setPendingUrlSelection(null);

    return () => {
      isActive = false;
    };
  }, [getDeterministicProcessId, pendingUrlSelection, processes, processesLoading, selectedProjectId]);

  useEffect(() => {
    if (!processes.length || pendingUrlSelection) return;
    if (!selectedProcessId || !processes.find((process) => process.id === selectedProcessId)) {
      const fallbackProcessId = getDeterministicProcessId();
      if (fallbackProcessId && fallbackProcessId !== selectedProcessId) {
        setSelectedProcessId(fallbackProcessId);
        setSelectedUseCaseId("");
        updateUrlSelection(fallbackProcessId, "");
      }
    }
  }, [getDeterministicProcessId, pendingUrlSelection, processes, selectedProcessId]);

  useEffect(() => {
    if (!selectedProcessId) {
      setProcessDetail(null);
      setUseCases([]);
      setSelectedUseCaseId("");
      updateUrlSelection("", "");
      return;
    }

    updateUrlSelection(selectedProcessId, selectedUseCaseId);

    const cached = processCache.current.get(selectedProcessId);
    if (cached) {
      setProcessDetail(cached);
      setProcessDetailError(null);
      setProcessDetailLoading(false);
    } else {
      setProcessDetailLoading(true);
      setProcessDetailError(null);
      fetchRoadmapProcess(selectedProcessId)
        .then((data) => {
          processCache.current.set(selectedProcessId, data);
          setProcessDetail(data);
        })
        .catch((error) => {
          console.error(error);
          setProcessDetailError("Impossible de charger le détail du processus.");
        })
        .finally(() => setProcessDetailLoading(false));
    }

    setUseCasesLoading(true);
    setUseCasesError(null);
    fetchRoadmapProcessUseCases(selectedProcessId)
      .then((data) => setUseCases(data))
      .catch((error) => {
        console.error(error);
        setUseCasesError("Impossible de charger les use cases.");
      })
      .finally(() => setUseCasesLoading(false));
  }, [selectedProcessId]);

  useEffect(() => {
    if (!selectedUseCaseId) {
      setUseCaseDetail(null);
      setUseCaseDetailError(null);
      return;
    }

    updateUrlSelection(selectedProcessId, selectedUseCaseId);

    const cached = useCaseCache.current.get(selectedUseCaseId);
    if (cached) {
      setUseCaseDetail(cached);
      setUseCaseDetailError(null);
      setUseCaseDetailLoading(false);
      return;
    }

    setUseCaseDetailLoading(true);
    setUseCaseDetailError(null);
    fetchRoadmapUseCase(selectedUseCaseId)
      .then((data) => {
        useCaseCache.current.set(selectedUseCaseId, data);
        setUseCaseDetail(data);
      })
      .catch((error) => {
        console.error(error);
        setUseCaseDetailError("Impossible de charger le use case.");
      })
      .finally(() => setUseCaseDetailLoading(false));
  }, [selectedUseCaseId, selectedProcessId]);

  useEffect(() => {
    if (!needsUseCaseValidation || !selectedUseCaseId || !selectedProcessId) return;
    if (useCasesLoading) return;
    const isPresent = useCases.some((useCase) => useCase.id === selectedUseCaseId);
    if (isPresent) {
      setNeedsUseCaseValidation(false);
      return;
    }
    let isActive = true;
    void fetchRoadmapUseCaseParentProcess(selectedUseCaseId)
      .then((parentProcessId) => {
        if (!isActive) return;
        if (parentProcessId && processes.some((process) => process.id === parentProcessId)) {
          setSelectedProcessId(parentProcessId);
          updateUrlSelection(parentProcessId, selectedUseCaseId);
        } else {
          const fallbackProcessId = getDeterministicProcessId();
          if (fallbackProcessId) {
            setSelectedProcessId(fallbackProcessId);
          }
          setSelectedUseCaseId("");
          updateUrlSelection(fallbackProcessId, "");
        }
        setNeedsUseCaseValidation(false);
      })
      .catch((error) => {
        console.error(error);
        if (!isActive) return;
        const fallbackProcessId = getDeterministicProcessId();
        if (fallbackProcessId) {
          setSelectedProcessId(fallbackProcessId);
        }
        setSelectedUseCaseId("");
        updateUrlSelection(fallbackProcessId, "");
        setNeedsUseCaseValidation(false);
      });
    return () => {
      isActive = false;
    };
  }, [
    getDeterministicProcessId,
    needsUseCaseValidation,
    processes,
    selectedProcessId,
    selectedUseCaseId,
    useCases,
    useCasesLoading,
  ]);

  useEffect(() => {
    if (!processes.length || !selectedProcessId) return;
    const index = processes.findIndex((process) => process.id === selectedProcessId);
    const neighbors = [processes[index - 1], processes[index + 1]].filter(Boolean) as RoadmapProcessSummary[];
    neighbors.forEach((neighbor) => {
      if (!processCache.current.has(neighbor.id)) {
        fetchRoadmapProcess(neighbor.id)
          .then((data) => processCache.current.set(neighbor.id, data))
          .catch((error) => console.error(error));
      }
    });
  }, [processes, selectedProcessId]);

  useEffect(() => {
    if (!selectedUseCaseId) return;
    const index = useCases.findIndex((useCase) => useCase.id === selectedUseCaseId);
    const neighbors = [useCases[index - 1], useCases[index + 1]].filter(Boolean) as RoadmapUseCaseSummary[];
    neighbors.forEach((neighbor) => {
      if (!useCaseCache.current.has(neighbor.id)) {
        fetchRoadmapUseCase(neighbor.id)
          .then((data) => useCaseCache.current.set(neighbor.id, data))
          .catch((error) => console.error(error));
      }
    });
  }, [useCases, selectedUseCaseId]);

  const processesById = useMemo(() => {
    return new Map(processes.map((process) => [process.id, process]));
  }, [processes]);

  const statusOptions = useMemo(() => {
    const unique = new Set(processes.map((process) => process.status).filter(Boolean) as string[]);
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [processes]);

  const ownerOptions = useMemo(() => {
    const unique = new Set(processes.map((process) => process.reviewOwner).filter(Boolean) as string[]);
    return Array.from(unique)
      .map((ownerId) => ({
        value: ownerId,
        label: resolveOwnerName(ownerId) ?? `Unknown user (id: ${ownerId})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [processes, resolveOwnerName]);

  const reviewOwnerOptions = useMemo(() => {
    const ownerMap = new Map<string, string>();
    roadmapUsers.forEach((user) => {
      const label = resolveOwnerName(user.id) ?? user.id;
      ownerMap.set(user.id, label);
      if (user.airtableRecordId) {
        ownerMap.set(user.airtableRecordId, label);
      }
    });
    processes.forEach((process) => {
      if (!process.reviewOwner) return;
      ownerMap.set(process.reviewOwner, resolveOwnerName(process.reviewOwner) ?? process.reviewOwner);
    });
    useCases.forEach((useCase) => {
      if (!useCase.reviewOwner) return;
      ownerMap.set(useCase.reviewOwner, resolveOwnerName(useCase.reviewOwner) ?? useCase.reviewOwner);
    });
    return Array.from(ownerMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [processes, resolveOwnerName, roadmapUsers, useCases]);

  const filteredProcesses = useMemo(() => {
    return processes.filter((process) => {
      if (statusFilter !== "all" && process.status !== statusFilter) return false;
      if (ownerFilter !== "all" && process.reviewOwner !== ownerFilter) return false;
      return true;
    });
  }, [ownerFilter, processes, statusFilter]);

  const { visibleProcesses, offsetTop, offsetBottom } = useMemo(() => {
    const total = filteredProcesses.length;
    const visibleCount = Math.ceil(viewportHeight / PROCESS_LIST_ROW_HEIGHT) + 8;
    const startIndex = Math.max(Math.floor(scrollTop / PROCESS_LIST_ROW_HEIGHT) - 4, 0);
    const endIndex = Math.min(startIndex + visibleCount, total);
    return {
      visibleProcesses: filteredProcesses.slice(startIndex, endIndex),
      offsetTop: startIndex * PROCESS_LIST_ROW_HEIGHT,
      offsetBottom: (total - endIndex) * PROCESS_LIST_ROW_HEIGHT,
    };
  }, [filteredProcesses, viewportHeight, scrollTop]);

  const handleProcessSelect = (processId: string) => {
    setSelectedProcessId(processId);
    setSelectedUseCaseId("");
    setProcessDialogOpen(true);
    setUseCaseDialogOpen(false);
  };

  const handleUseCaseSelect = (useCaseId: string) => {
    setSelectedUseCaseId(useCaseId);
    setUseCaseDialogOpen(true);
  };

  const handleUseCaseUpdated = (updatedUseCase: RoadmapUseCaseDetail) => {
    useCaseCache.current.set(updatedUseCase.id, updatedUseCase);
    setUseCaseDetail(updatedUseCase);
  };

  const handleCopyLink = (processId: string, useCaseId = "") => {
    updateUrlSelection(processId, useCaseId);
    void navigator.clipboard.writeText(window.location.href);
  };

  const handleExportPdf = async (processId: string) => {
    try {
      const blob = await exportRoadmapProcessPdf(processId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `roadmap-process-${processId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  const handleExportBriefPdf = async () => {
    if (!selectedProjectId) return;
    setBriefLoading(true);
    setAnalysisFieldsMigration(null);
    try {
      const stored = await fetchRoadmapAnalysisFields(selectedProjectId);
      const analysisFields: RoadmapBriefAnalysisFields = {
        processFields: Object.entries(stored.processFields)
          .filter(([, enabled]) => enabled)
          .map(([key]) => key),
        useCaseFields: Object.entries(stored.useCaseFields)
          .filter(([, enabled]) => enabled)
          .map(([key]) => key),
        processRawFields: stored.processRawFields ?? [],
        useCaseRawFields: stored.useCaseRawFields ?? [],
        includeRawFields: stored.includeRawFields !== false,
      };
      const blob = await exportRoadmapProjectBriefPdf(selectedProjectId, analysisFields);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `roadmap-portfolio-${selectedProjectId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setAnalysisFieldsMigration(error.details);
      } else {
        console.error(error);
      }
    } finally {
      setBriefLoading(false);
    }
  };

  const handleListKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!processes.length) return;
    const currentIndex = processes.findIndex((process) => process.id === selectedProcessId);
    if (event.key === "ArrowDown") {
      const next = processes[Math.min(currentIndex + 1, processes.length - 1)];
      if (next) handleProcessSelect(next.id);
      event.preventDefault();
    }
    if (event.key === "ArrowUp") {
      const prev = processes[Math.max(currentIndex - 1, 0)];
      if (prev) handleProcessSelect(prev.id);
      event.preventDefault();
    }
    if (event.key === "Enter" && selectedProcessId) {
      handleProcessSelect(selectedProcessId);
      event.preventDefault();
    }
  };

  const selectedProcessSummary = selectedProcessId ? processesById.get(selectedProcessId) : null;
  const userNameByRecordId = useMemo(() => {
    const entries = roadmapUsers.map((user) => {
      const name = user.displayName ?? "";
      const normalized = typeof name === "string" ? name.trim() : "";
      return user.airtableRecordId ? [user.airtableRecordId, normalized] : null;
    });
    return new Map(entries.filter(Boolean) as Array<[string, string]>);
  }, [roadmapUsers]);
  const systemNameByRecordId = useMemo(() => {
    const entries = [...roadmapApps, ...roadmapIntegrations].map((row) => {
      const raw = row.raw ?? {};
      const name =
        row.displayName ||
        (raw["Name"] as string | undefined) ||
        (raw["System"] as string | undefined) ||
        (raw["Software"] as string | undefined) ||
        (raw["Application"] as string | undefined) ||
        (raw["Application Name"] as string | undefined) ||
        (raw["App"] as string | undefined) ||
        (raw["App Name"] as string | undefined) ||
        (raw["Integration"] as string | undefined) ||
        (raw["Integration Name"] as string | undefined) ||
        "";
      const normalized = typeof name === "string" ? name.trim() : "";
      return row.airtableRecordId ? [row.airtableRecordId, normalized] : null;
    });
    return new Map(entries.filter(Boolean) as Array<[string, string]>);
  }, [roadmapApps, roadmapIntegrations]);
  const processNameByRecordId = useMemo(() => {
    const entries = roadmapProcessRecords.map((process) => {
      const name = typeof process.name === "string" ? process.name.trim() : "";
      return process.airtableRecordId ? [process.airtableRecordId, name || process.airtableRecordId] : null;
    });
    return new Map(entries.filter(Boolean) as Array<[string, string]>);
  }, [roadmapProcessRecords]);
  const useCaseNameByRecordId = useMemo(() => {
    const entries = roadmapUseCaseRecords.map((useCase) => {
      const name = typeof useCase.name === "string" ? useCase.name.trim() : "";
      return useCase.airtableRecordId ? [useCase.airtableRecordId, name || useCase.airtableRecordId] : null;
    });
    return new Map(entries.filter(Boolean) as Array<[string, string]>);
  }, [roadmapUseCaseRecords]);
  const resolveReference = useMemo(() => {
    const map = new Map([
      ...userNameByRecordId,
      ...systemNameByRecordId,
      ...processNameByRecordId,
      ...useCaseNameByRecordId,
    ]);
    return (value: string) => map.get(value) ?? value;
  }, [processNameByRecordId, systemNameByRecordId, useCaseNameByRecordId, userNameByRecordId]);
  const resolveRawReferences = useCallback(
    (value: unknown): unknown => {
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return value;
        if (trimmed.includes(",")) {
          return trimmed
            .split(",")
            .map((entry) => entry.trim())
            .filter(Boolean)
            .map((entry) => resolveReference(entry))
            .join(", ");
        }
        return resolveReference(trimmed);
      }
      if (Array.isArray(value)) {
        return value.map((entry) => resolveRawReferences(entry));
      }
      if (value && typeof value === "object") {
        return Object.fromEntries(
          Object.entries(value as Record<string, unknown>).map(([key, entry]) => [key, resolveRawReferences(entry)]),
        );
      }
      return value;
    },
    [resolveReference],
  );
  const normalizedProcessDetail = useMemo<ProcessDetailPanelData | null>(() => {
    if (!processDetail) return null;
    return {
      name: processDetail.name,
      purpose: processDetail.purpose,
      entity: processDetail.entity,
      department: processDetail.department,
      status: processDetail.status,
      applicability: processDetail.applicability,
      reviewStatus: processDetail.reviewStatus,
      reviewOwner: processDetail.reviewOwner ? resolveOwnerName(processDetail.reviewOwner) : null,
      reviewNotes: processDetail.reviewNotes,
      steps: processDetail.steps ?? [],
      risks: processDetail.risks ?? [],
      systems: (processDetail.systems ?? []).map((system) => resolveReference(system)),
      rawFields: (resolveRawReferences(processDetail.raw) as Record<string, unknown> | null) ?? null,
    };
  }, [processDetail, resolveOwnerName, resolveRawReferences, resolveReference]);
  const normalizedUseCaseDetail = useMemo<UseCaseDetailPanelData | null>(() => {
    if (!useCaseDetail) return null;
    return {
      id: useCaseDetail.id,
      name: useCaseDetail.name,
      problem: useCaseDetail.problem,
      outcome: useCaseDetail.outcome,
      aiOutputs: useCaseDetail.aiOutputs,
      humanValidation: useCaseDetail.humanValidation,
      acceptanceCriteria: useCaseDetail.acceptanceCriteria ?? [],
      aiPriorityScore: useCaseDetail.aiPriorityScore,
      opportunityScore: useCaseDetail.opportunityScore,
      impact: useCaseDetail.impact,
      feasibility: useCaseDetail.feasibility,
      interUseCaseBenefit: useCaseDetail.interUseCaseBenefit,
      risk: useCaseDetail.risk,
      status: useCaseDetail.status,
      estimatedDuration: useCaseDetail.estimatedDuration,
      estimatedCost: useCaseDetail.estimatedCost,
      plannedStart: useCaseDetail.plannedStart,
      plannedEnd: useCaseDetail.plannedEnd,
      businessScore: useCaseDetail.businessScore,
      itScore: useCaseDetail.itScore,
      complianceScore: useCaseDetail.complianceScore,
      standardScoreSummary: useCaseDetail.standardScoreSummary,
      reviewOwner: useCaseDetail.reviewOwner ? resolveOwnerName(useCaseDetail.reviewOwner) : null,
      reviewStatus: useCaseDetail.reviewStatus,
      reviewNotes: useCaseDetail.reviewNotes,
      reviewStage: useCaseDetail.reviewStage ?? null,
      reviewStageUpdatedAt: useCaseDetail.reviewStageUpdatedAt ?? null,
      reviewTransitions: useCaseDetail.reviewTransitions ?? [],
      reviewHistory: useCaseDetail.reviewHistory ?? null,
      jarvisExportStatus: useCaseDetail.jarvisExportStatus ?? null,
      jarvisExportError: useCaseDetail.jarvisExportError ?? null,
      jarvisExportedAt: useCaseDetail.jarvisExportedAt ?? null,
      dependencies: useCaseDetail.dependencies ?? [],
      dependents: useCaseDetail.dependents ?? [],
      targetArchitecture: useCaseDetail.targetArchitecture ?? null,
      notes: useCaseDetail.notes,
      validation: useCaseDetail.validation ?? null,
      auditEvents: useCaseDetail.auditEvents ?? [],
      rawFields: (resolveRawReferences(useCaseDetail.raw) as Record<string, unknown> | null) ?? null,
    };
  }, [resolveOwnerName, resolveRawReferences, useCaseDetail]);

  const sortedProcessFields = useMemo(
    () => [...processFieldConfig].sort((a, b) => a.order - b.order),
    [processFieldConfig],
  );
  const sortedUseCaseFields = useMemo(
    () => [...useCaseFieldConfig].sort((a, b) => a.order - b.order),
    [useCaseFieldConfig],
  );

  const filterFields = useCallback((fields: RoadmapReviewViewFieldConfig[], query: string) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return fields;
    return fields.filter((field) => {
      return field.label.toLowerCase().includes(normalized) || field.key.toLowerCase().includes(normalized);
    });
  }, []);

  const filteredProcessFields = useMemo(
    () => filterFields(sortedProcessFields, processFieldSearch),
    [filterFields, processFieldSearch, sortedProcessFields],
  );
  const filteredUseCaseFields = useMemo(
    () => filterFields(sortedUseCaseFields, useCaseFieldSearch),
    [filterFields, sortedUseCaseFields, useCaseFieldSearch],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Review workspace"
        description="Analysez les processus, validez les use cases et exportez des one-pagers."
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex min-w-[180px] flex-col gap-1 text-xs text-slate-500">
              Client
              <SelectInput
                value={selectedCustomerId}
                onChange={(event) => setSelectedCustomerId(event.target.value)}
              >
                <option value="">Sélectionner…</option>
                {customerOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectInput>
            </label>
            <label className="flex min-w-[200px] flex-col gap-1 text-xs text-slate-500">
              Projet
              <SelectInput
                value={selectedProjectId}
                onChange={(event) => setSelectedProjectId(event.target.value)}
                disabled={!projectOptions.length}
              >
                <option value="">Sélectionner…</option>
                {projectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectInput>
            </label>
            <div className="flex flex-col items-start gap-2 text-xs text-slate-500">
              <button
                type="button"
                onClick={handleExportBriefPdf}
                disabled={!selectedProjectId || briefLoading || Boolean(analysisFieldsMigration)}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {briefLoading ? "Génération..." : "Générer brief roadmap 1st review"}
              </button>
              <button
                type="button"
                onClick={() => void handleValidateFirstReview()}
                disabled={!selectedProjectId || !canReview}
                className="h-9 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-xs font-medium text-emerald-700 shadow-sm transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Validate 1st Review
              </button>
              {firstReviewGate?.validatedAt ? (
                <span className="text-[11px] text-emerald-600">
                  1st Review validé {firstReviewGate.forceValidated ? "(force)" : ""}
                </span>
              ) : null}
            </div>
            <div className="flex flex-col items-start gap-2 text-xs text-slate-500">
              <button
                type="button"
                onClick={() => void handleGenerateUseCaseDigests()}
                disabled={!selectedProjectId || digestRunning || Boolean(analysisFieldsMigration)}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {digestRunning ? "Génération..." : "Generate Use Case Digests"}
              </button>
              {digestStatusCounts ? (
                <div className="text-[11px] text-slate-600">
                  Digests • READY: {digestStatusCounts.READY ?? 0} • NEEDS_APPROVAL:{" "}
                  {digestStatusCounts.NEEDS_APPROVAL ?? 0} • FAIL: {digestStatusCounts.FAIL ?? 0} • DRAFT:{" "}
                  {digestStatusCounts.DRAFT ?? 0}
                </div>
              ) : null}
              {digestSummary ? (
                <div className="text-[11px] text-slate-500">
                  Dernier run • générés: {digestSummary.generated} • skip: {digestSummary.skipped} • fail:{" "}
                  {digestSummary.failed}
                </div>
              ) : null}
              {digestRecords.length ? (
                <div className="text-[11px] text-slate-500">Total digests: {digestRecords.length}</div>
              ) : null}
              {enrichmentError ? <div className="text-[11px] text-rose-600">{enrichmentError}</div> : null}
              {digestError ? <div className="text-[11px] text-rose-600">{digestError}</div> : null}
            </div>
          </div>
        }
      />

      <Dialog open={showValidationDialog} onOpenChange={(open) => setShowValidationDialog(open)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Validate 1st Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>Certains éléments ne sont pas encore validés. Confirmez-vous la validation de la 1st Review ?</p>
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {validationDetails
                ? `Processus non validés: ${validationDetails.processes.length} • Use cases non validés: ${validationDetails.useCases.length}`
                : `Validés: ${reviewMetrics.validated} / ${reviewMetrics.total} • En attente: ${reviewMetrics.pending}`}
            </div>
            {validationDetails ? (
              <div className="max-h-40 overflow-y-auto rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                <div className="font-semibold text-slate-700">Exemples</div>
                <ul className="mt-1 space-y-1">
                  {validationDetails.processes.slice(0, 5).map((process) => (
                    <li key={process.id}>
                      Process: {process.name ?? process.id} ({process.reviewStatus ?? "—"})
                    </li>
                  ))}
                  {validationDetails.useCases.slice(0, 5).map((useCase) => (
                    <li key={useCase.id}>
                      Use case: {useCase.name ?? useCase.id} ({useCase.reviewStatus ?? "—"})
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {validationError ? <div className="text-xs text-rose-600">{validationError}</div> : null}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="button button-secondary"
                onClick={() => setShowValidationDialog(false)}
              >
                Annuler
              </button>
              <button type="button" className="button button-primary" onClick={() => void handleForceValidateFirstReview()}>
                Validate anyway
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs
        value={activeSubTab}
        onValueChange={(value) => setActiveSubTab(value as "review" | "fields")}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="review">Review</TabsTrigger>
          <TabsTrigger value="fields">Fields</TabsTrigger>
        </TabsList>
        <TabsContent value="review" className="space-y-4">
          {analysisFieldsMigration ? <AtlasMigrationsRequiredCallout details={analysisFieldsMigration} /> : null}

          <div className="h-[calc(100vh-260px)]">
            <Pane className="overflow-hidden">
              <PaneHeader
                title="Processes"
                meta={`${filteredProcesses.length} items`}
                actions={
                  <div className="flex items-center gap-2">
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search"
                      className="h-8 w-40 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700"
                    />
                    <select
                      className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700"
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                    >
                      <option value="all">All statuses</option>
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                    <select
                      className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700"
                      value={ownerFilter}
                      onChange={(event) => setOwnerFilter(event.target.value)}
                    >
                      <option value="all">All owners</option>
                      {ownerOptions.map((owner) => (
                        <option key={owner.value} value={owner.value}>
                          {owner.label}
                        </option>
                      ))}
                    </select>
                    <select
                      className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700"
                      value={sort}
                      onChange={(event) => setSort(event.target.value)}
                    >
                      {SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                }
              />
              <div
                ref={listRef}
                className="flex-1 overflow-y-auto"
                tabIndex={0}
                onKeyDown={handleListKeyDown}
                onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
              >
                {processesLoading ? (
                  <Skeleton rows={6} />
                ) : processesError ? (
                  <EmptyState title={processesError} description="Réessayez dans quelques secondes." />
                ) : processes.length === 0 ? (
                  <EmptyState
                    title="Aucun processus"
                    description="Importez les données depuis Airtable pour commencer la revue."
                  />
                ) : filteredProcesses.length === 0 ? (
                  <EmptyState title="Aucun résultat" description="Aucun processus ne correspond aux filtres actifs." />
                ) : (
                  <div
                    className="divide-y divide-border/60"
                    style={{ paddingTop: offsetTop, paddingBottom: offsetBottom }}
                  >
                    {visibleProcesses.map((process) => (
                      <button
                        key={process.id}
                        type="button"
                        onClick={() => handleProcessSelect(process.id)}
                        className={`flex w-full items-start justify-between gap-2 px-4 py-3 text-left transition hover:bg-muted/60 ${
                          process.id === selectedProcessId ? "bg-muted" : ""
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-foreground">
                            {process.name ?? "Processus"}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {[process.entity, process.department, process.status].filter(Boolean).join(" • ") || "—"}
                          </div>
                        </div>
                        <Badge label={String(process.useCaseCount)} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Pane>
          </div>

          <Dialog
            open={processDialogOpen && Boolean(selectedProcessId)}
            onOpenChange={(open) => {
              setProcessDialogOpen(open);
              if (!open) {
                setUseCaseDialogOpen(false);
                setSelectedUseCaseId("");
              }
            }}
          >
            <DialogContent className="max-h-[90vh] w-full max-w-5xl overflow-hidden p-0">
              <DialogHeader className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-sm font-semibold text-foreground">
                    {processDetail?.name ?? "Process detail"}
                  </DialogTitle>
                  {processDetail?.status ? <Badge label={processDetail.status} /> : null}
                </div>
                <div className="flex items-center gap-2">
                  <IconButton label="Close" icon={<span>✕</span>} onClick={() => setProcessDialogOpen(false)} />
                  {processDetail ? (
                    <>
                      <IconButton
                        label="Copy deep link"
                        icon={<span>🔗</span>}
                        onClick={() => handleCopyLink(processDetail.id)}
                      />
                      <IconButton
                        label="Export PDF"
                        icon={<span>⬇️</span>}
                        onClick={() => handleExportPdf(processDetail.id)}
                      />
                    </>
                  ) : null}
                </div>
              </DialogHeader>
              <div className="max-h-[calc(90vh-72px)] overflow-y-auto">
                {processDetailLoading ? (
                  <Skeleton rows={8} />
                ) : processDetailError ? (
                  <EmptyState title={processDetailError} description="Réessayez ou choisissez un autre processus." />
                ) : !processDetail ? (
                  <EmptyState
                    title="Sélectionnez un processus"
                    description="Choisissez un processus dans la colonne de gauche pour démarrer la revue."
                  />
                ) : (
                  <>
                    {normalizedProcessDetail ? (
                      <ProcessDetailPanel
                        process={normalizedProcessDetail}
                        fields={processFieldConfig}
                        canEditReview={canReview}
                        reviewStatusOptions={REVIEW_STATUS_OPTIONS}
                        reviewOwnerOptions={reviewOwnerOptions}
                        onUpdateReview={handleUpdateProcessReview}
                        canEditDetails={canEdit}
                        onUpdateDetails={handleUpdateProcessDetails}
                        canViewRaw={isAdmin}
                      />
                    ) : null}
                    <Section title="Use cases liés">
                      {useCasesLoading ? (
                        <Skeleton rows={4} />
                      ) : useCasesError ? (
                        <div className="text-sm text-slate-500">{useCasesError}</div>
                      ) : useCases.length === 0 ? (
                        <div className="text-sm text-slate-500">Aucun use case lié pour l'instant.</div>
                      ) : (
                        <div className="space-y-2">
                          {useCases.map((useCase) => (
                            <button
                              key={useCase.id}
                              type="button"
                              className={`flex w-full items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-left text-sm transition hover:bg-slate-50 ${
                                useCase.id === selectedUseCaseId ? "bg-slate-100" : ""
                              }`}
                              onClick={() => handleUseCaseSelect(useCase.id)}
                            >
                              <div>
                                <div className="font-medium text-slate-800">{useCase.name ?? "Use case"}</div>
                                <div className="text-xs text-slate-500">
                                  {[useCase.status, useCase.reviewStatus].filter(Boolean).join(" • ") || "—"}
                                </div>
                              </div>
                              {useCase.opportunityScore != null ? (
                                <Badge label={`${useCase.opportunityScore}/100`} />
                              ) : null}
                            </button>
                          ))}
                        </div>
                      )}
                    </Section>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog
            open={useCaseDialogOpen && Boolean(selectedUseCaseId)}
            onOpenChange={(open) => {
              setUseCaseDialogOpen(open);
              if (!open) {
                setSelectedUseCaseId("");
              }
            }}
          >
            <DialogContent className="max-h-[90vh] w-full max-w-5xl overflow-hidden p-0">
              <DialogHeader className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-sm font-semibold text-foreground">
                    {useCaseDetail?.name ?? "Use case detail"}
                  </DialogTitle>
                  {useCaseDetail?.status ? <Badge label={useCaseDetail.status} /> : null}
                </div>
                <div className="flex items-center gap-2">
                  <IconButton label="Close" icon={<span>✕</span>} onClick={() => setUseCaseDialogOpen(false)} />
                  {useCaseDetail ? (
                    <IconButton
                      label="Copy deep link"
                      icon={<span>🔗</span>}
                      onClick={() => handleCopyLink(selectedProcessId, useCaseDetail.id)}
                    />
                  ) : null}
                </div>
              </DialogHeader>
              <div className="max-h-[calc(90vh-72px)] overflow-y-auto">
                {useCaseDetailLoading ? (
                  <Skeleton rows={8} />
                ) : useCaseDetailError ? (
                  <EmptyState title={useCaseDetailError} description="Réessayez dans quelques secondes." />
                ) : !useCaseDetail ? (
                  <EmptyState
                    title="Sélectionnez un use case"
                    description="Cliquez sur un use case pour ouvrir le détail."
                  />
                ) : (
                  <>
                    {normalizedUseCaseDetail ? (
                      <UseCaseDetailPanel
                        useCase={normalizedUseCaseDetail}
                        fields={useCaseFieldConfig}
                        canEditReview={canReview}
                        reviewStatusOptions={REVIEW_STATUS_OPTIONS}
                        reviewOwnerOptions={reviewOwnerOptions}
                        onUpdateReview={handleUpdateUseCaseReview}
                        onUpdateDetails={canEdit ? handleUpdateUseCaseDetails : undefined}
                        onSaveEstimates={canEdit ? handleSaveEstimates : undefined}
                        onExportToJarvis={canEdit ? handleJarvisExport : undefined}
                        jarvisExporting={jarvisExporting}
                        jarvisExportNotice={jarvisExportNotice}
                        jarvisExportError={jarvisExportError}
                        enrichment={enrichments.find((entry) => entry.useCaseId === useCaseDetail.id) ?? null}
                        canEditEnrichment={canEdit || canReview}
                        onUpdateEnrichmentAnswers={
                          canEdit || canReview
                            ? async (answers) => {
                                if (!useCaseDetail?.id) return;
                                const updated = await updateRoadmapUseCaseEnrichment(useCaseDetail.id, answers);
                                setEnrichments((prev) => [
                                  updated.enrichment,
                                  ...prev.filter((entry) => entry.useCaseId !== updated.enrichment.useCaseId),
                                ]);
                              }
                            : undefined
                        }
                        canViewRaw={isAdmin}
                      />
                    ) : null}
                    {jarvisExportPayload ? (
                      <Section title="Données Jarvis">
                        <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-foreground">
                          <div className="font-medium">Payload exporté</div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            Client: {jarvisExportPayload.customer} • Département: {jarvisExportPayload.department} •
                            Titre: {jarvisExportPayload.title}
                          </div>
                        </div>
                      </Section>
                    ) : null}
                    {useCaseDetail ? (
                      <Section title="Chat de revue">
                        <UseCaseConversationPanel
                          useCaseId={useCaseDetail.id}
                          onUseCaseUpdated={handleUseCaseUpdated}
                        />
                      </Section>
                    ) : null}
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <Divider />
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span>Keyboard: ↑ ↓ to navigate • Enter to select</span>
            {selectedProcessSummary?.opportunityScore != null ? (
              <Tag label={`Avg score ${Math.round(selectedProcessSummary.opportunityScore)}`} />
            ) : null}
          </div>
        </TabsContent>
        <TabsContent value="fields" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Configure which fields appear in the review panels for this Atlas project.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {reviewViewNotice ? <span className="text-xs text-emerald-600">{reviewViewNotice}</span> : null}
              {reviewViewError ? <span className="text-xs text-rose-600">{reviewViewError}</span> : null}
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleSaveReviewViewConfig()}
                disabled={!selectedProjectId || !reviewViewConfig || reviewViewSaving || !canEditReviewView}
              >
                {reviewViewSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="space-y-2">
                <CardTitle className="text-base">Process fields</CardTitle>
                <Input
                  placeholder="Search fields"
                  value={processFieldSearch}
                  onChange={(event) => setProcessFieldSearch(event.target.value)}
                />
              </CardHeader>
              <CardContent className="space-y-2">
                {filteredProcessFields.length ? (
                  <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
                    {filteredProcessFields.map((field) => {
                      const index = sortedProcessFields.findIndex((entry) => entry.key === field.key);
                      const isFirst = index <= 0;
                      const isLast = index === sortedProcessFields.length - 1;
                      return (
                        <div
                          key={field.key}
                          className="flex items-center gap-3 rounded-lg border border-border/60 bg-white p-2"
                        >
                          <div className="text-xs text-muted-foreground">≡</div>
                          <Checkbox
                            checked={field.enabled}
                            onChange={() => handleToggleField("process", field.key)}
                            disabled={!canEditReviewView}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-foreground">{field.label}</div>
                            <div className="truncate text-xs text-muted-foreground">{field.key}</div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveField("process", field.key, -1)}
                              disabled={!canEditReviewView || isFirst}
                            >
                              ↑
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveField("process", field.key, 1)}
                              disabled={!canEditReviewView || isLast}
                            >
                              ↓
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">No fields found.</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="space-y-2">
                <CardTitle className="text-base">Use case fields</CardTitle>
                <Input
                  placeholder="Search fields"
                  value={useCaseFieldSearch}
                  onChange={(event) => setUseCaseFieldSearch(event.target.value)}
                />
              </CardHeader>
              <CardContent className="space-y-2">
                {filteredUseCaseFields.length ? (
                  <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
                    {filteredUseCaseFields.map((field) => {
                      const index = sortedUseCaseFields.findIndex((entry) => entry.key === field.key);
                      const isFirst = index <= 0;
                      const isLast = index === sortedUseCaseFields.length - 1;
                      return (
                        <div
                          key={field.key}
                          className="flex items-center gap-3 rounded-lg border border-border/60 bg-white p-2"
                        >
                          <div className="text-xs text-muted-foreground">≡</div>
                          <Checkbox
                            checked={field.enabled}
                            onChange={() => handleToggleField("useCase", field.key)}
                            disabled={!canEditReviewView}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-foreground">{field.label}</div>
                            <div className="truncate text-xs text-muted-foreground">{field.key}</div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveField("useCase", field.key, -1)}
                              disabled={!canEditReviewView || isFirst}
                            >
                              ↑
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveField("useCase", field.key, 1)}
                              disabled={!canEditReviewView || isLast}
                            >
                              ↓
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">No fields found.</div>
                )}
              </CardContent>
            </Card>
          </div>
          {!canEditReviewView ? (
            <div className="text-xs text-muted-foreground">Vous avez un accès en lecture seule.</div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
